import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Download, FileText, Lightbulb, AlertTriangle, 
  Target, TrendingUp, Users, MapPin, CheckCircle, XCircle,
  BookOpen, Heart, Briefcase, RefreshCw, Printer, Map, Wrench,
  TrendingDown, GraduationCap, Building, UserCheck, AlertCircle,
  Calendar, School, Route, Laptop, UserPlus, ExternalLink,
  Home, Globe, BarChart3, Layers, Settings, Eye, EyeOff, Rocket,
  MessageCircle, Check, Flag, Compass, Brain, Monitor
} from 'lucide-react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Severity colors
const SEVERITY_COLORS = {
  success: { bg: '#E8F5E9', border: '#4CAF50', text: '#2E7D32' },
  warning: { bg: '#FFF3E0', border: '#FF9800', text: '#E65100' },
  info: { bg: '#E3F2FD', border: '#2196F3', text: '#1565C0' },
  high: { bg: '#FFEBEE', border: '#F44336', text: '#C62828' },
  medium: { bg: '#FFF3E0', border: '#FF9800', text: '#E65100' },
  low: { bg: '#E8F5E9', border: '#4CAF50', text: '#2E7D32' }
};

// Icon mapping
const ICON_MAP = {
  'alert-triangle': AlertTriangle,
  'users': Users,
  'wrench': Wrench,
  'lightbulb': Lightbulb,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  'map': MapPin,
  'target': Target
};

// Action icons mapping for specific Declic actions
const ACTION_ICON_MAP = {
  'calendar': Calendar,
  'school': School,
  'route': Route,
  'workshop': Wrench,
  'mentorship': UserPlus,
  'digital': Laptop
};

export default function AdvocacyPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dataVersions, setDataVersions] = useState([]);
  const [language, setLanguage] = useState('fr'); // Default to French
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  
  // Section visibility toggles
  const [visibleSections, setVisibleSections] = useState({
    keyFindings: true,
    belgianContext: true,
    skillsMarketGap: true,
    territorialAnalysis: true,
    keyObservations: true,
    imbalances: true,
    declicContribution: true,
    sources: true
  });
  
  // Settings panel visibility
  const [showSettings, setShowSettings] = useState(false);

  // Detect language from URL params on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    if (langParam && ['fr', 'nl'].includes(langParam)) {
      setLanguage(langParam);
    }
  }, []);

  useEffect(() => {
    fetchAdvocacyData();
  }, [language]); // Refetch when language changes

  const fetchAdvocacyData = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);
    try {
      const response = await axios.get(`${API}/analytics/advocacy?lang=${language}`);
      
      // Save version history
      if (data && isRefresh) {
        setDataVersions(prev => [
          ...prev.slice(-4), // Keep last 5 versions
          {
            timestamp: data.meta?.generated_at,
            data_version: data.meta?.data_version,
            total_sessions: data.meta?.total_sessions
          }
        ]);
      }
      
      setData(response.data);
      
      if (isRefresh) {
        toast.success('Données actualisées avec succès !', {
          description: `${response.data.meta?.total_sessions || 0} sessions analysées`,
          duration: 3000
        });
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement des données');
      console.error('Error fetching advocacy data:', err);
      if (isRefresh) {
        toast.error('Erreur lors de l\'actualisation', {
          description: err.response?.data?.detail || 'Veuillez réessayer',
          duration: 4000
        });
      }
    }
    setLoading(false);
    setRefreshing(false);
  };

  const handleRefresh = () => {
    fetchAdvocacyData(true);
  };

  const [exportingFormat, setExportingFormat] = useState(null);

  const exportDocument = async (format) => {
    setExportingFormat(format);
    const endpoints = {
      full: { url: `${API}/analytics/advocacy/export-pdf?lang=${language}`, filename: `declic-plaidoyer-complet` },
      executive: { url: `${API}/analytics/advocacy/export-executive-summary?lang=${language}`, filename: `declic-synthese-executive` },
      pitch: { url: `${API}/analytics/advocacy/export-pitch-deck?lang=${language}`, filename: `declic-pitch-deck` },
    };
    const { url, filename } = endpoints[format];
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${filename}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.success(language === 'fr' ? 'Document généré avec succès !' : 'Document succesvol gegenereerd!');
    } catch (err) {
      console.error('PDF export error:', err);
      toast.error(language === 'fr' ? 'Erreur lors de la génération' : 'Fout bij het genereren');
    }
    setExportingFormat(null);
  };

  const printPage = () => {
    window.print();
  };

  const loadPdfPreview = async () => {
    if (showPdfViewer) {
      setShowPdfViewer(false);
      return;
    }
    setPdfPreviewUrl(`${API}/analytics/advocacy/export-pdf?lang=${language}`);
    setShowPdfViewer(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-[#A4187A] animate-spin mx-auto mb-4" />
          <p className="text-[#1D1D1D]/60">Génération du plaidoyer en cours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={fetchAdvocacyData}
            className="px-4 py-2 bg-[#A4187A] text-white rounded-lg hover:bg-[#8E1569]"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F3E5F5] to-[#EBEBEB]">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-gradient-to-r from-[#A4187A] to-[#8E24AA] text-white py-6 px-4 no-print">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
                title="Retour à l'accueil"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold">{data?.meta?.title || 'Plaidoyer Déclic'}</h1>
                <p className="text-white/70 text-sm">{data?.meta?.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Language Selector */}
              <div className="flex items-center gap-1 bg-white/20 rounded-lg p-1">
                <button
                  onClick={() => setLanguage('fr')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${language === 'fr' ? 'bg-white text-[#A4187A]' : 'hover:bg-white/20'}`}
                  data-testid="lang-fr-btn"
                >
                  FR
                </button>
                <button
                  onClick={() => setLanguage('nl')}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${language === 'nl' ? 'bg-white text-[#A4187A]' : 'hover:bg-white/20'}`}
                  data-testid="lang-nl-btn"
                >
                  NL
                </button>
              </div>
              {/* Settings button */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${showSettings ? 'bg-white text-[#A4187A]' : 'bg-white/20 hover:bg-white/30'}`}
                title={language === 'fr' ? "Paramètres d'affichage" : "Weergave-instellingen"}
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'fr' ? 'Sections' : 'Secties'}</span>
              </button>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-4 py-2 bg-[#FB6823] rounded-lg hover:bg-[#E55A1B] transition-colors flex items-center gap-2 font-medium disabled:opacity-70"
                data-testid="refresh-advocacy-data"
                title={language === 'fr' ? "Actualiser les données et l'analyse" : "Gegevens en analyse vernieuwen"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{refreshing ? (language === 'fr' ? 'Actualisation...' : 'Vernieuwen...') : (language === 'fr' ? 'Actualiser' : 'Vernieuwen')}</span>
              </button>
              <button
                onClick={printPage}
                className="px-4 py-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                <span className="hidden sm:inline">{language === 'fr' ? 'Imprimer' : 'Afdrukken'}</span>
              </button>
              <button
                onClick={() => exportDocument('full')}
                disabled={exportingFormat !== null}
                className="px-4 py-2 bg-white rounded-lg text-[#A4187A] hover:bg-white/90 transition-colors flex items-center gap-2 font-medium"
                data-testid="export-advocacy-pdf"
              >
                <Download className={`w-4 h-4 ${exportingFormat === 'full' ? 'animate-bounce' : ''}`} />
                <span className="hidden sm:inline">PDF</span>
              </button>
            </div>
          </div>
        </div>
      </header>
      
      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white border-b border-[#E0E0E0] py-4 px-4 no-print">
          <div className="max-w-5xl mx-auto">
            <h3 className="font-semibold text-[#1D1D1D] mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4 text-[#A4187A]" />
              Sélectionnez les sections à afficher
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'keyFindings', label: 'Points clés à retenir' },
                { key: 'belgianContext', label: 'Contexte belge' },
                { key: 'skillsMarketGap', label: 'Décalage filières/marché' },
                { key: 'territorialAnalysis', label: 'Analyse territoriale' },
                { key: 'keyObservations', label: 'Observations clés' },
                { key: 'imbalances', label: 'Déséquilibres' },
                { key: 'declicContribution', label: 'Actions Déclic' },
                { key: 'sources', label: 'Sources et références' }
              ].map(section => (
                <label 
                  key={section.key}
                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                    visibleSections[section.key] ? 'bg-[#A4187A]/10 border border-[#A4187A]' : 'bg-gray-100 border border-gray-200'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={visibleSections[section.key]}
                    onChange={() => setVisibleSections(prev => ({...prev, [section.key]: !prev[section.key]}))}
                    className="w-4 h-4 text-[#A4187A] rounded focus:ring-[#A4187A]"
                  />
                  <span className={`text-sm ${visibleSections[section.key] ? 'text-[#A4187A] font-medium' : 'text-gray-500'}`}>
                    {section.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-5xl mx-auto p-4 space-y-6 print:p-0">
        
        {/* Meta Info */}
        <div className="text-center text-sm text-[#1D1D1D]/60 print:mb-4">
          <p>
            Analyse générée le {data?.meta?.generated_at ? new Date(data.meta.generated_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : new Date().toLocaleDateString('fr-FR')} 
            {' | '}{data?.meta?.total_sessions || 0} sessions analysées
            {data?.meta?.data_version && ` | Version: ${data.meta.data_version}`}
          </p>
          
          {/* Version History (if available) */}
          {dataVersions.length > 0 && (
            <details className="mt-2 text-xs">
              <summary className="cursor-pointer hover:text-[#A4187A]">
                Historique des versions ({dataVersions.length})
              </summary>
              <div className="mt-2 bg-white rounded-lg p-3 border border-gray-200 inline-block text-left">
                {dataVersions.map((v, idx) => (
                  <div key={idx} className="py-1 border-b border-gray-100 last:border-0">
                    <span className="text-[#1D1D1D]/70">
                      {new Date(v.timestamp).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <span className="text-[#A4187A] ml-2">{v.total_sessions} sessions</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        {/* METHODOLOGICAL DISCLAIMER (NEW) */}
        {data?.methodology_disclaimer && (
          <section className="bg-blue-50 rounded-xl p-5 border border-blue-200">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-700 mb-2">{data.methodology_disclaimer.title}</h3>
                <p className="text-sm text-blue-600" dangerouslySetInnerHTML={{ __html: data.methodology_disclaimer.content?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                <p className="text-xs text-blue-500 mt-2 italic">{data.methodology_disclaimer.data_status?.message}</p>
              </div>
            </div>
          </section>
        )}

        {/* NARRATIVE QUOTE (NEW - Impactful opening) */}
        {data?.narrative_quote && (
          <section className="bg-gradient-to-r from-[#A4187A] to-[#8E24AA] rounded-xl p-8 text-white text-center">
            <blockquote className="text-xl md:text-2xl font-medium italic mb-4">
              "{data.narrative_quote.quote}"
            </blockquote>
            <p className="text-white/80 text-sm">
              {data.narrative_quote.subquote}
            </p>
          </section>
        )}

        {/* INTRODUCTION SECTION (NEW - PART 3) */}
        {data?.introduction && (
          <section className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm">
            <h2 className="text-xl font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#A4187A]" />
              {data.introduction.title}
            </h2>
            <div 
              className="prose prose-sm max-w-none text-[#1D1D1D]/80"
              dangerouslySetInnerHTML={{ 
                __html: data.introduction.content
                  ?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  ?.replace(/→/g, '<span class="text-[#A4187A] font-medium">→</span>')
                  ?.replace(/\n/g, '<br/>')
              }} 
            />
          </section>
        )}

        {/* DÉCLIC ECOSYSTEM SECTION (NEW) */}
        {data?.declic_ecosystem && (
          <section className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl p-6 border-2 border-[#A4187A]">
            {/* Positioning Statement */}
            <div className="bg-gradient-to-r from-[#A4187A] to-[#6A1B5D] text-white rounded-lg p-5 mb-6">
              <p className="text-lg font-medium text-center">
                {data.declic_ecosystem.positioning_statement}
              </p>
            </div>

            <h2 className="text-xl font-bold text-[#1D1D1D] mb-2 flex items-center gap-2">
              <Layers className="w-6 h-6 text-[#A4187A]" />
              {data.declic_ecosystem.title}
            </h2>
            <p className="text-sm text-[#1D1D1D]/70 mb-6">{data.declic_ecosystem.intro}</p>

            {/* Ecosystem vs Tool Distinction */}
            {data.declic_ecosystem.ecosystem_vs_tool && (
              <div className="bg-white rounded-lg p-4 mb-6 border border-purple-200">
                <h3 className="font-semibold text-[#1D1D1D] mb-3">{data.declic_ecosystem.ecosystem_vs_tool.title}</h3>
                <p className="text-sm text-[#1D1D1D]/70 mb-4">{data.declic_ecosystem.ecosystem_vs_tool.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#A4187A]/10 rounded-lg p-3 border-l-4 border-[#A4187A]">
                    <p className="font-semibold text-[#A4187A]">{data.declic_ecosystem.ecosystem_vs_tool.ecosystem?.name}</p>
                    <p className="text-xs text-[#1D1D1D]/70">{data.declic_ecosystem.ecosystem_vs_tool.ecosystem?.description}</p>
                  </div>
                  <div className="bg-[#FB6823]/10 rounded-lg p-3 border-l-4 border-[#FB6823]">
                    <p className="font-semibold text-[#FB6823]">{data.declic_ecosystem.ecosystem_vs_tool.tool?.name}</p>
                    <p className="text-xs text-[#1D1D1D]/70">{data.declic_ecosystem.ecosystem_vs_tool.tool?.description}</p>
                  </div>
                </div>
              </div>
            )}

            {/* 4 Pillars */}
            {data.declic_ecosystem.pillars && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#1D1D1D] mb-4">{language === 'fr' ? 'Les 4 piliers de l\'écosystème' : 'De 4 pijlers van het ecosysteem'}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {data.declic_ecosystem.pillars.map((pillar, idx) => {
                    const IconComponent = {
                      'compass': Compass,
                      'brain': Brain,
                      'users': Users,
                      'laptop': Laptop
                    }[pillar.icon] || Compass;
                    
                    return (
                      <div 
                        key={idx} 
                        className="bg-white rounded-lg p-4 text-center shadow-sm border-t-4"
                        style={{ borderColor: pillar.color }}
                      >
                        <div 
                          className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                          style={{ backgroundColor: `${pillar.color}20` }}
                        >
                          <IconComponent className="w-6 h-6" style={{ color: pillar.color }} />
                        </div>
                        <h4 className="font-semibold text-sm" style={{ color: pillar.color }}>{pillar.title}</h4>
                        <p className="text-xs text-[#1D1D1D]/60 mt-1">{pillar.description}</p>
                        <div className="mt-2 space-y-1">
                          {pillar.activities?.slice(0, 2).map((activity, i) => (
                            <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded-full inline-block m-0.5">
                              {activity}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PART 2: Restructured "Actions concrètes" with 3 sections */}
            {data.declic_ecosystem.actions_concretes && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#1D1D1D] mb-4">{data.declic_ecosystem.actions_concretes.title}</h3>
                <div className="space-y-6">
                  {data.declic_ecosystem.actions_concretes.sections?.map((section, sectionIdx) => {
                    const SectionIcon = {
                      'users': Users,
                      'compass': Compass,
                      'globe': Globe
                    }[section.icon] || Users;
                    
                    return (
                      <div 
                        key={sectionIdx} 
                        className={`rounded-lg p-5 border-l-4 ${section.is_primary ? 'bg-white shadow-sm' : 'bg-gray-50'}`}
                        style={{ borderColor: section.color }}
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${section.color}20` }}
                          >
                            <SectionIcon className="w-5 h-5" style={{ color: section.color }} />
                          </div>
                          <div>
                            <h4 className="font-semibold" style={{ color: section.color }}>{section.title}</h4>
                            {section.note && (
                              <p className="text-xs text-[#1D1D1D]/50 italic">{section.note}</p>
                            )}
                          </div>
                          {!section.is_primary && (
                            <span className="ml-auto text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                              {language === 'fr' ? 'Support' : 'Ondersteuning'}
                            </span>
                          )}
                        </div>
                        
                        <div className={`grid gap-4 ${section.items?.length > 1 ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
                          {section.items?.map((item, itemIdx) => {
                            const ItemIcon = {
                              'school': School,
                              'route': Route,
                              'calendar': Calendar,
                              'laptop': Laptop,
                              'globe': Globe
                            }[item.icon] || School;
                            
                            return (
                              <div 
                                key={itemIdx} 
                                className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow"
                                style={{ borderColor: `${section.color}40` }}
                              >
                                <div className="flex gap-3">
                                  <div 
                                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                    style={{ backgroundColor: `${section.color}15` }}
                                  >
                                    <ItemIcon className="w-5 h-5" style={{ color: section.color }} />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-semibold text-sm text-[#1D1D1D]">{item.name}</p>
                                    <p className="text-xs text-[#1D1D1D]/60 mt-1">{item.description}</p>
                                    {item.impact && (
                                      <p className="text-xs mt-2 px-2 py-1 rounded-full inline-block" 
                                         style={{ backgroundColor: `${section.color}10`, color: section.color }}>
                                        ✓ {item.impact}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Fallback: Original activities structure */}
            {!data.declic_ecosystem.actions_concretes && data.declic_ecosystem.activities && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#1D1D1D] mb-4">{data.declic_ecosystem.activities.title}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Human Activities */}
                  <div>
                    <h4 className="font-medium text-[#A4187A] mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      {data.declic_ecosystem.activities.human_activities?.title}
                    </h4>
                    <div className="space-y-3">
                      {data.declic_ecosystem.activities.human_activities?.items?.map((item, idx) => {
                        const IconComponent = {
                          'school': School,
                          'route': Route,
                          'calendar': Calendar,
                          'laptop': Laptop
                        }[item.icon] || School;
                        
                        return (
                          <div key={idx} className="bg-white rounded-lg p-3 border border-purple-100 flex gap-3">
                            <div className="w-10 h-10 bg-[#A4187A]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-5 h-5 text-[#A4187A]" />
                            </div>
                            <div>
                              <p className="font-semibold text-sm text-[#1D1D1D]">{item.name}</p>
                              <p className="text-xs text-[#1D1D1D]/60">{item.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Digital Activities */}
                  <div>
                    <h4 className="font-medium text-[#FB6823] mb-3 flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      {data.declic_ecosystem.activities.digital_activities?.title}
                    </h4>
                    <div className="space-y-3">
                      {data.declic_ecosystem.activities.digital_activities?.items?.map((item, idx) => (
                        <div key={idx} className="bg-white rounded-lg p-3 border border-orange-100 flex gap-3">
                          <div className="w-10 h-10 bg-[#FB6823]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Globe className="w-5 h-5 text-[#FB6823]" />
                          </div>
                          <div>
                            <p className="font-semibold text-sm text-[#1D1D1D]">{item.name}</p>
                            <p className="text-xs text-[#1D1D1D]/60">{item.description}</p>
                            <span className="text-xs bg-[#FB6823]/10 text-[#FB6823] px-2 py-0.5 rounded-full mt-1 inline-block">
                              {language === 'fr' ? 'Digital' : 'Digitaal'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Digital Skills Section */}
            {data.declic_ecosystem.digital_skills && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-5 border border-green-200">
                <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <Laptop className="w-5 h-5" />
                  {data.declic_ecosystem.digital_skills.title}
                </h3>
                <p className="text-sm text-[#1D1D1D]/70 mb-4">{data.declic_ecosystem.digital_skills.intro}</p>
                <div className="space-y-2 mb-4">
                  {data.declic_ecosystem.digital_skills.objectives?.map((obj, idx) => (
                    <p key={idx} className="text-sm text-green-700">{obj}</p>
                  ))}
                </div>
                <div className="bg-green-100 rounded-lg p-3">
                  <p className="text-sm text-green-800 font-medium">{data.declic_ecosystem.digital_skills.impact}</p>
                </div>
              </div>
            )}
          </section>
        )}

        {/* MAJOR INSIGHTS (NEW - PART 2) */}
        {visibleSections.keyFindings && data?.major_insights?.insights?.length > 0 && (
          <section className="bg-white rounded-xl p-6 border-2 border-[#A4187A] shadow-lg">
            <h2 className="text-xl font-bold text-[#A4187A] mb-2 flex items-center gap-2">
              <Target className="w-6 h-6" />
              {data.major_insights.title}
            </h2>
            <p className="text-sm text-[#1D1D1D]/60 mb-4">{data.major_insights.intro}</p>
            
            <div className="space-y-4">
              {data.major_insights.insights.map((insight, idx) => {
                const severityColors = {
                  high: { bg: '#FFEBEE', border: '#F44336', text: '#C62828' },
                  medium: { bg: '#FFF3E0', border: '#FF9800', text: '#E65100' },
                  positive: { bg: '#E8F5E9', border: '#4CAF50', text: '#2E7D32' }
                };
                const colors = severityColors[insight.severity] || severityColors.medium;
                
                return (
                  <div 
                    key={idx}
                    className="rounded-lg p-4 border-l-4"
                    style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                  >
                    <h3 className="font-semibold mb-2" style={{ color: colors.text }}>
                      {insight.title}
                    </h3>
                    {insight.data_point && (
                      <p className="text-sm font-medium text-[#1D1D1D] mb-2">
                        📊 {insight.data_point}
                      </p>
                    )}
                    <p className="text-sm text-[#1D1D1D]/70 italic">
                      → {insight.interpretation}
                    </p>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* CE QUE RÉVÈLE DÉCLIC (NEW - PART 4) */}
        {data?.declic_reveals && (
          <section className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-6 border border-purple-200">
            <h2 className="text-xl font-bold text-[#A4187A] mb-2 flex items-center gap-2">
              <Lightbulb className="w-6 h-6" />
              {data.declic_reveals.title}
            </h2>
            <p className="text-sm text-[#1D1D1D]/70 mb-2">{data.declic_reveals.intro}</p>
            <p className="text-xs text-[#A4187A] mb-4 font-medium">{data.declic_reveals.data_note}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.declic_reveals.revelations?.map((rev, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 border border-purple-100 shadow-sm">
                  <h3 className="font-semibold text-[#A4187A] mb-2">{rev.title}</h3>
                  <p className="text-sm text-[#1D1D1D]/80 mb-2">{rev.finding}</p>
                  <p className="text-xs text-[#1D1D1D]/60 italic mb-2">{rev.data_link}</p>
                  <div className="bg-[#A4187A]/10 rounded px-3 py-2">
                    <p className="text-xs text-[#A4187A] font-medium">→ {rev.implication}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* IMPLICATIONS ET RISQUES (NEW - PART 5) */}
        {data?.implications_risks && (
          <section className="bg-red-50 rounded-xl p-6 border border-red-200">
            <h2 className="text-xl font-bold text-red-700 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              {data.implications_risks.title}
            </h2>
            <p className="text-sm text-red-600/80 mb-4">{data.implications_risks.intro}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.implications_risks.implications?.map((impl, idx) => (
                <div 
                  key={idx} 
                  className={`bg-white rounded-lg p-4 border-l-4 ${impl.severity === 'high' ? 'border-red-500' : 'border-orange-400'}`}
                >
                  <h3 className="font-semibold text-[#1D1D1D] mb-2">{impl.title}</h3>
                  <p className="text-sm text-[#1D1D1D]/70 mb-2">{impl.description}</p>
                  <p className="text-xs text-red-600 font-medium">⚠️ {impl.consequence}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* POURQUOI AGIR MAINTENANT (NEW - PART 6) */}
        {data?.urgency && (
          <section className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-6 text-white">
            <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
              <AlertCircle className="w-6 h-6" />
              {data.urgency.title}
            </h2>
            <p className="text-lg font-medium mb-4 opacity-90">{data.urgency.main_message}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.urgency.arguments?.map((arg, idx) => (
                <div key={idx} className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                  <h3 className="font-semibold mb-1">{arg.title}</h3>
                  <p className="text-sm opacity-90">{arg.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Key Findings Box - OLD (keep for backward compatibility but hidden by default) */}
        {false && visibleSections.keyFindings && data?.key_findings?.length > 0 && (
          <section className="bg-white rounded-xl p-6 border-2 border-[#A4187A] shadow-lg" data-testid="key-findings">
            <h2 className="text-xl font-bold text-[#A4187A] mb-4 flex items-center gap-2">
              <Lightbulb className="w-6 h-6" />
              Points clés à retenir
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.key_findings.map((finding, idx) => {
                const IconComponent = ICON_MAP[finding.icon] || Lightbulb;
                const colors = SEVERITY_COLORS[finding.severity] || SEVERITY_COLORS.info;
                return (
                  <div 
                    key={idx}
                    className="p-4 rounded-lg border-l-4"
                    style={{ 
                      backgroundColor: colors.bg, 
                      borderColor: colors.border 
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <IconComponent className="w-5 h-5 mt-0.5" style={{ color: colors.border }} />
                      <div>
                        <h3 className="font-semibold" style={{ color: colors.text }}>
                          {finding.title}
                        </h3>
                        <p className="text-sm text-[#1D1D1D]/70 mt-1">{finding.text}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* SECTION 1: BELGIAN CONTEXT */}
        {visibleSections.belgianContext && data?.belgian_context && (
          <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
              <Map className="w-5 h-5 text-[#A4187A]" />
              {data.belgian_context.title}
            </h2>
            
            <div className="space-y-6">
              {data.belgian_context.sections?.map((section, idx) => (
                <div key={idx} className="border-l-4 border-[#FB6823] pl-4">
                  <h3 className="font-semibold text-[#FB6823] mb-2">{section.title}</h3>
                  <p className="text-[#1D1D1D]/80 mb-4 whitespace-pre-line">{section.content}</p>
                  
                  {/* Stats Grid */}
                  {section.stats && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                      {section.stats.map((stat, i) => {
                        const colors = SEVERITY_COLORS[stat.severity] || SEVERITY_COLORS.info;
                        return (
                          <div 
                            key={i} 
                            className="p-3 rounded-lg border-l-4 text-center"
                            style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                          >
                            <div className="text-2xl font-bold" style={{ color: colors.text }}>
                              {stat.value}
                            </div>
                            <div className="text-xs text-[#1D1D1D]/70">{stat.label}</div>
                            {stat.source && (
                              <div className="text-xs text-[#1D1D1D]/50 mt-1">{stat.source}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Inequality Factors (NEW - for network/socio-economic section) */}
                  {section.inequality_factors && (
                    <div className="space-y-4 mb-4">
                      {section.inequality_factors.map((factor, i) => {
                        const colors = SEVERITY_COLORS[factor.severity] || SEVERITY_COLORS.medium;
                        return (
                          <div 
                            key={i} 
                            className="rounded-lg p-4 border-l-4"
                            style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center flex-shrink-0" style={{ borderColor: colors.border, borderWidth: 2 }}>
                                {factor.icon === 'map-pin' && <MapPin className="w-5 h-5" style={{ color: colors.border }} />}
                                {factor.icon === 'users' && <Users className="w-5 h-5" style={{ color: colors.border }} />}
                                {factor.icon === 'wrench' && <Wrench className="w-5 h-5" style={{ color: colors.border }} />}
                                {factor.icon === 'target' && <Target className="w-5 h-5" style={{ color: colors.border }} />}
                              </div>
                              <div className="flex-1">
                                <h4 className="font-semibold text-[#1D1D1D]">{factor.title}</h4>
                                <p className="text-sm text-[#1D1D1D]/70 mt-1">{factor.description}</p>
                                {factor.impact && (
                                  <p className="text-xs mt-2 font-medium" style={{ color: colors.text }}>
                                    ⚠️ Impact : {factor.impact}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Obstacles List */}
                  {section.obstacles && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {section.obstacles.map((obstacle, i) => (
                        <div key={i} className="bg-gray-50 rounded-lg p-3">
                          <h4 className="font-medium text-[#1D1D1D] flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-[#F44336]" />
                            {obstacle.title}
                          </h4>
                          <p className="text-sm text-[#1D1D1D]/70 mt-1">{obstacle.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Consequences */}
                  {section.consequences && (
                    <div className="space-y-2">
                      {section.consequences.map((cons, i) => {
                        const Icon = ICON_MAP[cons.icon] || AlertTriangle;
                        return (
                          <div key={i} className="flex items-center gap-3 p-2 bg-red-50 rounded-lg">
                            <Icon className="w-5 h-5 text-red-500 flex-shrink-0" />
                            <span className="text-sm text-[#1D1D1D]/80">{cons.text}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SECTION 1bis: SKILLS MARKET GAP */}
        {visibleSections.skillsMarketGap && data?.skills_market_gap && (
          <section className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 border-2 border-red-200">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#F44336] text-white rounded-full flex items-center justify-center">
                <TrendingDown className="w-4 h-4" />
              </span>
              <TrendingDown className="w-5 h-5 text-[#F44336]" />
              {data.skills_market_gap.title}
            </h2>
            
            <p className="text-[#1D1D1D]/80 mb-6 whitespace-pre-line">{data.skills_market_gap.intro}</p>
            
            {/* Market Data Stats */}
            {data.skills_market_gap.market_data && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#F44336] mb-3">{data.skills_market_gap.market_data.title}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {data.skills_market_gap.market_data.stats?.map((stat, i) => {
                    const colors = SEVERITY_COLORS[stat.severity] || SEVERITY_COLORS.medium;
                    return (
                      <div 
                        key={i} 
                        className="p-3 rounded-lg border-l-4 text-center bg-white"
                        style={{ borderColor: colors.border }}
                      >
                        <div className="text-2xl font-bold" style={{ color: colors.text }}>
                          {stat.value}
                        </div>
                        <div className="text-xs text-[#1D1D1D]/70">{stat.label}</div>
                        <div className="text-xs text-[#1D1D1D]/50 mt-1">{stat.source}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Sectors in Tension */}
            {data.skills_market_gap.sectors_in_tension && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#F44336] mb-2">{data.skills_market_gap.sectors_in_tension.title}</h3>
                <p className="text-sm text-[#1D1D1D]/60 mb-4">{data.skills_market_gap.sectors_in_tension.description}</p>
                
                <div className="space-y-3">
                  {data.skills_market_gap.sectors_in_tension.sectors?.map((sector, i) => {
                    const gapColors = {
                      critique: { bg: '#FFEBEE', border: '#F44336', text: '#C62828' },
                      important: { bg: '#FFF3E0', border: '#FF9800', text: '#E65100' },
                      modéré: { bg: '#E3F2FD', border: '#2196F3', text: '#1565C0' }
                    };
                    const colors = gapColors[sector.gap] || gapColors.modéré;
                    
                    return (
                      <div key={i} className="bg-white rounded-lg p-4 border" style={{ borderColor: colors.border }}>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                          <h4 className="font-semibold text-[#1D1D1D]">{sector.name}</h4>
                          <span 
                            className="text-xs font-bold px-2 py-1 rounded-full"
                            style={{ backgroundColor: colors.bg, color: colors.text }}
                          >
                            Décalage {sector.gap}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm mb-2">
                          <div>
                            <span className="text-[#1D1D1D]/50">Besoin marché :</span>{' '}
                            <span className="font-medium text-[#F44336]">{sector.market_need}</span>
                          </div>
                          <div>
                            <span className="text-[#1D1D1D]/50">Intérêt jeunes :</span>{' '}
                            <span className="font-medium" style={{ color: colors.text }}>{sector.youth_interest}</span>
                          </div>
                        </div>
                        
                        <div className="text-xs text-[#1D1D1D]/60 mb-2">
                          <strong>Métiers :</strong> {sector.jobs?.join(', ')}
                        </div>
                        
                        {sector.why_unpopular && (
                          <div className="text-xs bg-gray-100 rounded p-2 text-[#1D1D1D]/70">
                            <strong>Pourquoi peu attractif :</strong> {sector.why_unpopular}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Popular vs Saturated */}
            {data.skills_market_gap.popular_vs_saturated && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#FF9800] mb-2">{data.skills_market_gap.popular_vs_saturated.title}</h3>
                <p className="text-sm text-[#1D1D1D]/60 mb-4">{data.skills_market_gap.popular_vs_saturated.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.skills_market_gap.popular_vs_saturated.examples?.map((example, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 border border-orange-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-[#1D1D1D]">{example.field}</span>
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">{example.job_market}</span>
                      </div>
                      <div className="text-xs text-[#1D1D1D]/60">
                        <span className="text-green-600">Demande jeunes : {example.youth_demand}</span>
                      </div>
                      <div className="text-xs text-[#F44336] mt-1">{example.reality}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Declic Insights */}
            {data.skills_market_gap.declic_insights?.findings?.length > 0 && (
              <div className="mb-6 bg-[#A4187A]/10 rounded-lg p-4 border border-[#A4187A]/30">
                <h3 className="font-semibold text-[#A4187A] mb-3 flex items-center gap-2">
                  <span className="text-lg">📊</span>
                  {data.skills_market_gap.declic_insights.title}
                </h3>
                <p className="text-sm text-[#1D1D1D]/70 mb-4">{data.skills_market_gap.declic_insights.description}</p>
                
                <div className="space-y-4">
                  {data.skills_market_gap.declic_insights.findings.map((finding, i) => (
                    <div key={i} className="bg-white rounded-lg p-3 border-l-4 border-[#A4187A]">
                      <h4 className="font-medium text-[#A4187A] mb-2">{finding.title}</h4>
                      
                      {Array.isArray(finding.data) && (
                        <div className="flex flex-wrap gap-2 mb-2">
                          {finding.data.map((item, j) => (
                            <span key={j} className="text-xs bg-[#A4187A]/10 text-[#A4187A] px-2 py-1 rounded">
                              {item}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {typeof finding.data === 'object' && !Array.isArray(finding.data) && (
                        <div className="flex flex-wrap gap-3 mb-2 text-sm">
                          {Object.entries(finding.data).map(([key, value], j) => (
                            <span key={j} className="text-[#1D1D1D]/70">
                              <strong className="text-[#A4187A]">{key}:</strong> {value}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      <p className="text-xs text-[#1D1D1D]/60 italic">{finding.interpretation}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Root Causes */}
            {data.skills_market_gap.root_causes && (
              <div className="bg-white rounded-lg p-4 border border-red-200">
                <h3 className="font-semibold text-[#F44336] mb-3">{data.skills_market_gap.root_causes.title}</h3>
                <div className="space-y-3">
                  {data.skills_market_gap.root_causes.causes?.map((cause, i) => (
                    <div key={i} className="border-l-4 border-red-300 pl-3">
                      <h4 className="font-medium text-[#1D1D1D]">{cause.cause}</h4>
                      <p className="text-sm text-[#1D1D1D]/70">{cause.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* SECTION 1ter: TERRITORIAL ANALYSIS */}
        {visibleSections.territorialAnalysis && data?.territorial_analysis && (
          <section className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-200">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#2196F3] text-white rounded-full flex items-center justify-center">
                <MapPin className="w-4 h-4" />
              </span>
              <Globe className="w-5 h-5 text-[#2196F3]" />
              {data.territorial_analysis.title}
            </h2>
            
            <p className="text-[#1D1D1D]/80 mb-6 whitespace-pre-line">{data.territorial_analysis.intro}</p>
            
            {/* Brussels Region */}
            {data.territorial_analysis.brussels_region && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#2196F3] mb-2 flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  {data.territorial_analysis.brussels_region.title}
                </h3>
                <p className="text-sm text-[#1D1D1D]/60 mb-4">{data.territorial_analysis.brussels_region.description}</p>
                
                {/* Brussels Global Stats */}
                {data.territorial_analysis.brussels_region.global_stats && (
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-white rounded-lg p-3 text-center border border-blue-200">
                      <div className="text-2xl font-bold text-[#F44336]">{data.territorial_analysis.brussels_region.global_stats.youth_unemployment}</div>
                      <div className="text-xs text-[#1D1D1D]/60">Chômage jeunes</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-blue-200">
                      <div className="text-2xl font-bold text-[#FF9800]">{data.territorial_analysis.brussels_region.global_stats.school_dropout}</div>
                      <div className="text-xs text-[#1D1D1D]/60">Décrochage scolaire</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-blue-200">
                      <div className="text-2xl font-bold text-[#2196F3]">{data.territorial_analysis.brussels_region.global_stats.population_under_25}</div>
                      <div className="text-xs text-[#1D1D1D]/60">Pop. &lt; 25 ans</div>
                    </div>
                  </div>
                )}
                
                {/* Brussels Communes Grid - Only show stats (no Declic data since empty) */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.territorial_analysis.brussels_region.communes?.map((commune, idx) => {
                    const priorityColors = {
                      haute: { bg: '#FFEBEE', border: '#F44336', badge: 'bg-red-500' },
                      moyenne: { bg: '#FFF3E0', border: '#FF9800', badge: 'bg-orange-500' }
                    };
                    const colors = priorityColors[commune.priority] || priorityColors.moyenne;
                    
                    return (
                      <div 
                        key={idx} 
                        className="bg-white rounded-lg p-4 border-l-4 shadow-sm"
                        style={{ borderColor: colors.border }}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-[#1D1D1D] text-sm">{commune.name}</h4>
                          <span className={`text-xs text-white px-2 py-0.5 rounded ${colors.badge}`}>
                            {commune.priority}
                          </span>
                        </div>
                        
                        {/* Context Stats - Key metrics only */}
                        {commune.context && (
                          <div className="space-y-1 text-xs mb-2">
                            <div className="flex justify-between">
                              <span className="text-[#1D1D1D]/50">Pop. jeune</span>
                              <span className="font-medium">{commune.context.population_jeune}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#1D1D1D]/50">Chômage jeunes</span>
                              <span className="font-medium text-[#F44336]">{commune.context.taux_chomage_jeunes}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-[#1D1D1D]/50">ISE</span>
                              <span className="font-medium">{commune.context.ise_rank}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Challenges - Show first one only */}
                        {commune.challenges?.length > 0 && (
                          <div className="text-xs text-[#1D1D1D]/60 italic">
                            → {commune.challenges[0]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Wallonia Priority - Simplified stats only */}
            {data.territorial_analysis.wallonia_priority && (
              <div className="mb-6">
                <h3 className="font-semibold text-[#FF9800] mb-2 flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  {data.territorial_analysis.wallonia_priority.title}
                </h3>
                <p className="text-sm text-[#1D1D1D]/60 mb-4">{data.territorial_analysis.wallonia_priority.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {data.territorial_analysis.wallonia_priority.communes?.map((commune, idx) => (
                    <div key={idx} className="bg-white rounded-lg p-3 border border-orange-200">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-[#1D1D1D]">{commune.name}</h4>
                        <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                          {commune.priority}
                        </span>
                      </div>
                      
                      {commune.context && (
                        <div className="text-xs text-[#1D1D1D]/60 space-y-1">
                          <div>👥 {commune.context.population_jeune}</div>
                          <div className="text-[#F44336]">📉 Chômage jeunes: {commune.context.taux_chomage_jeunes}</div>
                          <div>🏭 {commune.context.secteur_dominant}</div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Territorial Recommendations */}
            {data.territorial_analysis.territorial_recommendations && (
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <h3 className="font-semibold text-[#2196F3] mb-3">
                  {data.territorial_analysis.territorial_recommendations.title}
                </h3>
                <div className="space-y-3">
                  {data.territorial_analysis.territorial_recommendations.items?.map((item, i) => {
                    const priorityStyle = item.priority === 'haute' 
                      ? 'border-red-300 bg-red-50' 
                      : 'border-orange-300 bg-orange-50';
                    return (
                      <div key={i} className={`border-l-4 pl-3 py-2 rounded-r ${priorityStyle}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                            item.priority === 'haute' ? 'bg-red-500 text-white' : 'bg-orange-500 text-white'
                          }`}>
                            {item.priority.toUpperCase()}
                          </span>
                          <h4 className="font-medium text-[#1D1D1D] text-sm">{item.action}</h4>
                        </div>
                        <p className="text-xs text-[#1D1D1D]/70">{item.description}</p>
                        <p className="text-xs text-green-600 mt-1">✓ Impact attendu : {item.impact_attendu}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {/* OLD SECTION 2: INTRODUCTION - REMOVED (moved to top of page) */}

        {/* SECTION 3: KEY OBSERVATIONS */}
        {visibleSections.keyObservations && data?.key_observations?.sections?.length > 0 && (
          <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
              <Target className="w-5 h-5 text-[#A4187A]" />
              {data.key_observations.title}
            </h2>
            <div className="space-y-6">
              {data.key_observations.sections.map((section, idx) => (
                <div key={idx} className="border-l-4 border-[#2196F3] pl-4">
                  <h3 className="font-semibold text-[#2196F3] mb-2">{section.title}</h3>
                  <p className="text-[#1D1D1D]/80 mb-3">{section.content}</p>
                  {section.insight && (
                    <p className="text-sm text-[#1D1D1D]/60 italic bg-blue-50 p-2 rounded">
                      💡 {section.insight}
                    </p>
                  )}
                  {section.data?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {section.data.map((item, i) => (
                        <span 
                          key={i}
                          className="px-3 py-1 bg-[#F3E5F5] text-[#A4187A] rounded-full text-sm font-medium"
                        >
                          {item.name}: {item.value}{typeof item.value === 'number' && item.value <= 100 ? '%' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SECTION 4: SHORTAGE CAREERS */}
        {data?.shortage_careers && (
          <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
              <Wrench className="w-5 h-5 text-[#A4187A]" />
              {data.shortage_careers.title}
            </h2>
            <p className="text-[#1D1D1D]/80 mb-4">{data.shortage_careers.content}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.shortage_careers.shortage_sectors?.map((sector, idx) => {
                const attractivityColors = {
                  low: { bg: '#FFEBEE', text: '#C62828', label: 'Peu attractif' },
                  medium: { bg: '#FFF3E0', text: '#E65100', label: 'Moyennement attractif' },
                  high: { bg: '#E8F5E9', text: '#2E7D32', label: 'Attractif' }
                };
                const colors = attractivityColors[sector.attractivity] || attractivityColors.medium;
                
                return (
                  <div key={idx} className="border rounded-lg p-4" style={{ backgroundColor: colors.bg }}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-[#1D1D1D]">{sector.sector}</h4>
                      <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'white', color: colors.text }}>
                        {colors.label}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {sector.jobs?.map((job, i) => (
                        <span key={i} className="text-xs bg-white/70 px-2 py-1 rounded">
                          {job}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {data.shortage_careers.analysis && (
              <div className="mt-4 p-4 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
                <p className="text-[#1D1D1D]/80">
                  <strong>Analyse :</strong> {data.shortage_careers.analysis}
                </p>
              </div>
            )}
          </section>
        )}

        {/* SECTION 5: IMBALANCES */}
        {visibleSections.imbalances && data?.imbalances?.items?.length > 0 && (
          <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
              <AlertTriangle className="w-5 h-5 text-[#A4187A]" />
              {data.imbalances.title}
            </h2>
            <div className="space-y-4">
              {data.imbalances.items.map((item, idx) => {
                const colors = SEVERITY_COLORS[item.severity] || SEVERITY_COLORS.medium;
                return (
                  <div 
                    key={idx}
                    className="p-4 rounded-lg border-l-4"
                    style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                  >
                    <h3 className="font-semibold mb-1" style={{ color: colors.text }}>
                      {item.title}
                    </h3>
                    <p className="text-[#1D1D1D]/80 text-sm">{item.description}</p>
                    {item.solution && (
                      <p className="text-sm mt-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-700">Solution : {item.solution}</span>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* SECTION 6: TOOL IMPACT */}
        {data?.tool_impact && (
          <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
              <TrendingUp className="w-5 h-5 text-[#A4187A]" />
              {data.tool_impact.title}
            </h2>
            {data.tool_impact.description && (
              <p className="text-[#1D1D1D]/80 mb-4">{data.tool_impact.description}</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(data.tool_impact.metrics || {}).map(([key, metric], idx) => (
                <div key={idx} className="bg-gradient-to-br from-[#F3E5F5] to-white rounded-lg p-4 text-center border border-[#E0E0E0]">
                  <div className="text-3xl font-bold text-[#A4187A]">
                    {typeof metric.value === 'number' ? `${metric.value}%` : metric.value}
                  </div>
                  <div className="text-sm font-medium text-[#1D1D1D] mt-1">{metric.label}</div>
                  <div className="text-xs text-[#1D1D1D]/60 mt-1">{metric.description}</div>
                  {metric.interpretation && (
                    <div className="text-xs text-[#A4187A] mt-2 italic">{metric.interpretation}</div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SECTION 7: CHALLENGES */}
        {data?.challenges?.items?.length > 0 && (
          <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-sm font-bold">7</span>
              <AlertTriangle className="w-5 h-5 text-[#A4187A]" />
              {data.challenges.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.challenges.items.map((item, idx) => {
                const Icon = ICON_MAP[item.icon] || AlertTriangle;
                return (
                  <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start gap-3">
                      <Icon className="w-5 h-5 text-[#F44336] mt-0.5 flex-shrink-0" />
                      <div>
                        <h3 className="font-semibold text-[#1D1D1D]">{item.title}</h3>
                        <p className="text-sm text-[#1D1D1D]/70 mt-1">{item.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* SECTION 8: GENERAL RECOMMENDATIONS (NEUTRAL) */}
        {data?.general_recommendations && (
          <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-sm font-bold">8</span>
              <Lightbulb className="w-5 h-5 text-[#A4187A]" />
              {data.general_recommendations.title}
            </h2>
            
            {data.general_recommendations.intro && (
              <p className="text-[#1D1D1D]/80 mb-6">{data.general_recommendations.intro}</p>
            )}
            
            <div className="space-y-6">
              {data.general_recommendations.categories?.map((category, idx) => (
                <div key={idx} className="border-l-4 border-[#4CAF50] pl-4">
                  <h3 className="font-semibold text-[#4CAF50] mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    {category.title}
                  </h3>
                  <div className="space-y-3">
                    {category.items?.map((item, i) => {
                      const priorityColors = {
                        high: { bg: '#FFEBEE', border: '#F44336', tag: 'Priorité haute' },
                        medium: { bg: '#FFF3E0', border: '#FF9800', tag: 'Priorité moyenne' },
                        low: { bg: '#E8F5E9', border: '#4CAF50', tag: 'Priorité basse' }
                      };
                      const colors = priorityColors[item.priority] || priorityColors.medium;
                      
                      return (
                        <div 
                          key={i} 
                          className="p-4 rounded-lg border-l-4"
                          style={{ backgroundColor: colors.bg, borderColor: colors.border }}
                        >
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <h4 className="font-medium text-[#1D1D1D]">{item.title}</h4>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white" style={{ color: colors.border }}>
                              {colors.tag}
                            </span>
                          </div>
                          <p className="text-sm text-[#1D1D1D]/70 mb-2">{item.description}</p>
                          {item.target && (
                            <p className="text-xs text-[#1D1D1D]/50">
                              <strong>Cibles :</strong> {item.target}
                            </p>
                          )}
                          {item.impact && (
                            <p className="text-xs text-green-700 mt-1">
                              <strong>Impact attendu :</strong> {item.impact}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SECTION 9: DECLIC CONTRIBUTION - Redesigned */}
        {visibleSections.declicContribution && data?.declic_contribution && (
          <section className="bg-gradient-to-br from-[#F3E5F5] to-white rounded-xl p-6 border border-[#E0E0E0]">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#A4187A] text-white rounded-full flex items-center justify-center text-sm font-bold">9</span>
              <span className="text-2xl">⭐</span>
              {data.declic_contribution.title}
            </h2>
            
            {data.declic_contribution.intro && (
              <p className="text-[#1D1D1D]/80 mb-6">{data.declic_contribution.intro}</p>
            )}
            
            {/* Two Main Pillars - Like in the image */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Un tremplin vers l'action */}
              {data.declic_contribution.contributions?.find(c => c.icon === 'rocket') && (
                <div className="bg-white rounded-xl p-5 border-l-4 border-[#FB6823] shadow-sm">
                  <h3 className="font-bold text-[#FB6823] mb-3 flex items-center gap-2">
                    <Rocket className="w-5 h-5" />
                    Un tremplin vers l'action
                  </h3>
                  <p className="text-sm text-[#1D1D1D]/80 mb-4">
                    Déclic ne s'arrête pas au diagnostic : il encourage les jeunes à passer à l'action en rencontrant des professionnels et en explorant les formations.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="text-sm text-[#1D1D1D]/70 flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                      Incitation à rencontrer des professionnels (Journées Déclic)
                    </li>
                    <li className="text-sm text-[#1D1D1D]/70 flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                      Liens vers les formations et parcours d'études
                    </li>
                    <li className="text-sm text-[#1D1D1D]/70 flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                      Accompagnement post-test pour approfondir les pistes
                    </li>
                  </ul>
                  <div className="bg-[#FB6823]/10 rounded-lg px-4 py-2 text-sm text-[#FB6823]">
                    <Check className="w-4 h-4 inline mr-1" />
                    Score d'engagement : {data.declic_contribution.contributions?.find(c => c.icon === 'rocket')?.data_point?.match(/\d+/)?.[0] || '100'}% souhaitent poursuivre leur découverte
                  </div>
                </div>
              )}
              
              {/* Un support pour les acteurs de l'orientation */}
              {data.declic_contribution.contributions?.find(c => c.icon === 'users') && (
                <div className="bg-white rounded-xl p-5 border-l-4 border-[#2196F3] shadow-sm">
                  <h3 className="font-bold text-[#2196F3] mb-3 flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    Un support pour les acteurs de l'orientation
                  </h3>
                  <p className="text-sm text-[#1D1D1D]/80 mb-4">
                    Déclic fournit aux conseillers PMS, enseignants et accompagnateurs un outil objectif pour initier le dialogue sur l'orientation.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="text-sm text-[#1D1D1D]/70 flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                      Point de départ neutre pour la discussion
                    </li>
                    <li className="text-sm text-[#1D1D1D]/70 flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                      Visualisation des résultats partageable
                    </li>
                    <li className="text-sm text-[#1D1D1D]/70 flex items-start gap-2">
                      <Check className="w-4 h-4 text-[#4CAF50] mt-0.5 flex-shrink-0" />
                      Tableaux de bord pour analyser les tendances par école ou région
                    </li>
                  </ul>
                  <div className="bg-[#2196F3]/10 rounded-lg px-4 py-2 text-sm text-[#2196F3]">
                    <Check className="w-4 h-4 inline mr-1" />
                    Plaidoyer et rapports exportables pour les décideurs
                  </div>
                </div>
              )}
            </div>
            
            {/* Les actions concrètes de Déclic - Grid like in image */}
            {data.declic_contribution.specific_actions && (
              <div className="bg-white rounded-xl p-6 border border-[#E0E0E0]">
                <h3 className="font-bold text-[#A4187A] mb-2 flex items-center gap-2">
                  <span className="text-xl">🎬</span>
                  {data.declic_contribution.specific_actions.title}
                </h3>
                <p className="text-sm text-[#1D1D1D]/60 mb-6">
                  {data.declic_contribution.specific_actions.description}
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.declic_contribution.specific_actions.items?.map((action, idx) => {
                    const ActionIcon = ACTION_ICON_MAP[action.icon] || Target;
                    const colors = [
                      { bg: 'bg-[#A4187A]', light: 'bg-[#A4187A]/10', text: 'text-[#A4187A]' },
                      { bg: 'bg-[#FB6823]', light: 'bg-[#FB6823]/10', text: 'text-[#FB6823]' },
                      { bg: 'bg-[#2196F3]', light: 'bg-[#2196F3]/10', text: 'text-[#2196F3]' },
                      { bg: 'bg-[#9C27B0]', light: 'bg-[#9C27B0]/10', text: 'text-[#9C27B0]' },
                      { bg: 'bg-[#00BCD4]', light: 'bg-[#00BCD4]/10', text: 'text-[#00BCD4]' },
                      { bg: 'bg-[#4CAF50]', light: 'bg-[#4CAF50]/10', text: 'text-[#4CAF50]' }
                    ];
                    const color = colors[idx % colors.length];
                    
                    return (
                      <div key={idx} className="bg-[#F9F9F9] rounded-xl p-4 border border-[#E0E0E0] hover:shadow-md transition-shadow">
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 ${color.bg} rounded-full flex items-center justify-center`}>
                            <ActionIcon className="w-5 h-5 text-white" />
                          </div>
                          <h4 className={`font-bold ${color.text}`}>{action.name}</h4>
                        </div>
                        
                        <p className="text-sm text-[#1D1D1D]/70 mb-3">{action.description}</p>
                        
                        {/* Show details list if available */}
                        {action.details?.length > 0 && (
                          <ul className="space-y-1 mb-3">
                            {action.details.slice(0, 4).map((detail, i) => (
                              <li key={i} className="text-xs text-[#1D1D1D]/60 flex items-start gap-1">
                                <span className={color.text}>•</span>
                                {detail}
                              </li>
                            ))}
                          </ul>
                        )}
                        
                        <div className={`${color.light} rounded-lg px-3 py-2 text-xs ${color.text} font-medium`}>
                          <Check className="w-3 h-3 inline mr-1" />
                          {action.impact}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Call to Action - Comment déployer Déclic ? - IMPROVED VERSION */}
            {data.declic_contribution.call_to_action && (
              <div className="bg-gradient-to-br from-[#F8F9FA] to-white rounded-xl p-6 border-2 border-[#FB6823]/30 mt-6">
                {/* Header with icon */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#FB6823] to-[#E55A1B] rounded-xl flex items-center justify-center shadow-lg">
                    <Rocket className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-xl text-[#FB6823]">
                      {data.declic_contribution.call_to_action.title}
                    </h3>
                  </div>
                </div>
                
                {/* PART 5: Introduction */}
                {data.declic_contribution.call_to_action.introduction && (
                  <div className="bg-[#FB6823]/5 border-l-4 border-[#FB6823] px-4 py-3 rounded-r-lg mb-4">
                    <p className="text-[#1D1D1D]/80 font-medium">
                      {data.declic_contribution.call_to_action.introduction}
                    </p>
                  </div>
                )}
                
                {/* PART 7: Positioning */}
                {data.declic_contribution.call_to_action.positioning && (
                  <p className="text-sm text-[#1D1D1D]/60 italic mb-6 text-center">
                    💡 {data.declic_contribution.call_to_action.positioning}
                  </p>
                )}
                
                {/* 4 Blocks - PART 1 Structure */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {data.declic_contribution.call_to_action.items?.map((item, idx) => {
                    const blockColors = [
                      { border: '#A4187A', bg: 'from-[#A4187A]/5 to-white', icon: '🎓' },
                      { border: '#FB6823', bg: 'from-[#FB6823]/5 to-white', icon: '🚀' },
                      { border: '#2196F3', bg: 'from-[#2196F3]/5 to-white', icon: '📊' },
                      { border: '#4CAF50', bg: 'from-[#4CAF50]/5 to-white', icon: '👥' }
                    ];
                    const color = blockColors[idx % blockColors.length];
                    
                    return (
                      <div 
                        key={idx} 
                        className={`bg-gradient-to-br ${color.bg} rounded-xl p-5 border-l-4 hover:shadow-lg transition-all duration-300`}
                        style={{ borderColor: color.border }}
                      >
                        {/* Block Header */}
                        <div className="flex items-start gap-3 mb-3">
                          <span className="text-2xl">{color.icon}</span>
                          <div className="flex-1">
                            <h4 className="font-bold text-[#1D1D1D]" style={{ color: color.border }}>
                              {item.action}
                            </h4>
                          </div>
                        </div>
                        
                        {/* Description */}
                        <p className="text-sm text-[#1D1D1D]/70 mb-4 leading-relaxed">
                          {item.description}
                        </p>
                        
                        {/* PART 3: Key Actions with arrows */}
                        {item.key_actions && item.key_actions.length > 0 && (
                          <div className="bg-white rounded-lg p-3 mb-3 space-y-2">
                            {item.key_actions.map((action, actionIdx) => (
                              <p 
                                key={actionIdx} 
                                className="text-sm text-[#1D1D1D]/80 leading-relaxed"
                              >
                                {action}
                              </p>
                            ))}
                          </div>
                        )}
                        
                        {/* Target */}
                        {item.target && (
                          <div className="flex items-start gap-2 mb-2">
                            <span className="text-xs font-bold text-[#1D1D1D]/50 whitespace-nowrap">Cible :</span>
                            <span className="text-xs text-[#1D1D1D]/70">{item.target}</span>
                          </div>
                        )}
                        
                        {/* Impact */}
                        {item.impact && (
                          <div 
                            className="rounded-lg px-3 py-2 text-xs font-medium mt-3"
                            style={{ backgroundColor: `${color.border}15`, color: color.border }}
                          >
                            ✓ {item.impact}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {/* SECTION 10: CONCLUSION STRATÉGIQUE AMÉLIORÉE (PART 4) */}
        {data?.strategic_conclusion && (
          <section className="bg-gradient-to-br from-[#A4187A] to-[#6A1B5D] rounded-xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Flag className="w-7 h-7" />
              {data.strategic_conclusion.title}
            </h2>
            
            {/* Intro Statement */}
            <p className="text-lg mb-6 opacity-95 leading-relaxed">{data.strategic_conclusion.intro}</p>
            
            {/* Key Findings */}
            <div className="bg-white/10 rounded-lg p-5 mb-6">
              <div className="space-y-2">
                {data.strategic_conclusion.key_findings?.map((finding, idx) => (
                  <p key={idx} className="text-white/90">{finding}</p>
                ))}
              </div>
            </div>
            
            {/* Belgian Context */}
            {data.strategic_conclusion.belgian_context && (
              <div className="mb-6">
                <p className="text-white/80 mb-3">{data.strategic_conclusion.belgian_context.intro}</p>
                <div className="bg-white/10 rounded-lg p-4 space-y-2">
                  {data.strategic_conclusion.belgian_context.points?.map((point, idx) => (
                    <p key={idx} className="text-white/90">{point}</p>
                  ))}
                </div>
              </div>
            )}
            
            {/* Risks */}
            {data.strategic_conclusion.risks && (
              <div className="mb-6">
                <p className="text-white/80 mb-3">{data.strategic_conclusion.risks.intro}</p>
                <div className="bg-red-500/30 border border-red-300/30 rounded-lg p-4 space-y-2">
                  {data.strategic_conclusion.risks.points?.map((point, idx) => (
                    <p key={idx} className="text-white/90">{point}</p>
                  ))}
                </div>
              </div>
            )}
            
            {/* Déclic Solution */}
            {data.strategic_conclusion.declic_solution && (
              <div className="bg-white/20 rounded-lg p-5">
                <p className="font-semibold text-lg mb-3">{data.strategic_conclusion.declic_solution.intro}</p>
                <p className="text-white/80 mb-3">{data.strategic_conclusion.declic_solution.transformation}</p>
                
                <div className="bg-white/10 rounded-lg p-4 mb-4 space-y-2">
                  {data.strategic_conclusion.declic_solution.from_to?.map((line, idx) => (
                    <p key={idx} className="text-white font-medium">{line}</p>
                  ))}
                </div>
                
                <p className="text-white/80 mb-2">{data.strategic_conclusion.declic_solution.positioning}</p>
                <div className="space-y-2">
                  {data.strategic_conclusion.declic_solution.roles?.map((role, idx) => (
                    <p key={idx} className="text-white/90 font-medium">{role}</p>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* PART 12: STRONG CONCLUSION - Mandatory Structured Conclusion */}
        {data?.strong_conclusion && (
          <section className="bg-gradient-to-br from-[#1D1D1D] to-[#2D2D2D] rounded-xl p-6 text-white print:break-before-page">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <Flag className="w-7 h-7 text-[#FB6823]" />
                {data.strong_conclusion.title}
              </h2>
              {data.strong_conclusion.is_mandatory && (
                <span className="text-xs bg-[#FB6823] text-white px-3 py-1 rounded-full">
                  {language === 'fr' ? 'Section obligatoire' : 'Verplichte sectie'}
                </span>
              )}
            </div>
            
            <div className="space-y-6">
              {data.strong_conclusion.structure?.map((section, idx) => {
                const SectionIcon = {
                  'target': Target,
                  'eye-off': EyeOff,
                  'map': MapPin,
                  'alert-triangle': AlertTriangle,
                  'rocket': Rocket
                }[section.icon] || Target;
                
                const bgColors = [
                  'from-purple-900/50 to-purple-800/30',
                  'from-orange-900/50 to-orange-800/30',
                  'from-blue-900/50 to-blue-800/30',
                  'from-red-900/50 to-red-800/30',
                  'from-green-900/50 to-green-800/30'
                ];
                
                return (
                  <div 
                    key={idx}
                    className={`bg-gradient-to-r ${bgColors[idx % bgColors.length]} rounded-lg p-5 border border-white/10`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                        <SectionIcon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-white">{section.title}</h3>
                    </div>
                    <div className="text-white/90 text-sm whitespace-pre-line leading-relaxed pl-13">
                      {section.content}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Signature Sentence */}
            {data.strong_conclusion.signature_sentence && (
              <div className="mt-8 text-center">
                <blockquote className="text-xl italic text-[#FB6823] font-medium">
                  "{data.strong_conclusion.signature_sentence}"
                </blockquote>
              </div>
            )}
            
            {/* Call to Action */}
            {data.strong_conclusion.call_to_action && (
              <div className="mt-6 text-center">
                <p className="text-lg font-bold text-white bg-gradient-to-r from-[#A4187A] to-[#FB6823] inline-block px-6 py-3 rounded-full">
                  {data.strong_conclusion.call_to_action}
                </p>
              </div>
            )}
          </section>
        )}

        {/* ONE-PAGE SUMMARY (NEW - PART 7) */}
        {data?.one_page_summary && (
          <section className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border-2 border-blue-300 print:break-before-page">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-blue-800 flex items-center gap-2">
                <FileText className="w-6 h-6" />
                {data.one_page_summary.title}
              </h2>
              <span className="text-xs bg-blue-500 text-white px-3 py-1 rounded-full">
                {language === 'fr' ? 'Format 1 page' : '1-pagina formaat'}
              </span>
            </div>
            <p className="text-blue-600 text-sm mb-6">{data.one_page_summary.subtitle}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Key Figures */}
              {data.one_page_summary.sections?.key_figures && (
                <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-200">
                  <h3 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    {data.one_page_summary.sections.key_figures.title}
                  </h3>
                  <div className="space-y-2">
                    {data.one_page_summary.sections.key_figures.items?.map((item, idx) => (
                      <div key={idx} className={`flex justify-between items-center ${item.highlight ? 'bg-blue-50 p-2 rounded-lg' : ''}`}>
                        <span className="text-sm text-[#1D1D1D]/80">{item.label || item}</span>
                        <span className={`text-sm font-semibold ${item.highlight ? 'text-blue-700' : 'text-[#1D1D1D]'}`}>
                          {item.value || ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Main Insights */}
              {data.one_page_summary.sections?.main_insights && (
                <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-200">
                  <h3 className="font-semibold text-blue-700 mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    {data.one_page_summary.sections.main_insights.title}
                  </h3>
                  <div className="space-y-2">
                    {data.one_page_summary.sections.main_insights.items?.map((item, idx) => (
                      <p key={idx} className="text-sm text-[#1D1D1D]/80">
                        {typeof item === 'object' ? item.text : item}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Implications */}
              {data.one_page_summary.sections?.implications && (
                <div className="bg-white rounded-lg p-4 shadow-sm border border-orange-200">
                  <h3 className="font-semibold text-orange-600 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {data.one_page_summary.sections.implications.title}
                  </h3>
                  <div className="space-y-2">
                    {data.one_page_summary.sections.implications.items?.map((item, idx) => (
                      <p key={idx} className="text-sm text-[#1D1D1D]/80">
                        {typeof item === 'object' ? item.text : item}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Déclic Solution */}
              {data.one_page_summary.sections?.declic_solution && (
                <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200">
                  <h3 className="font-semibold text-green-600 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    {data.one_page_summary.sections.declic_solution.title}
                  </h3>
                  {data.one_page_summary.sections.declic_solution.intro && (
                    <p className="text-sm text-green-700 font-medium mb-2">
                      {data.one_page_summary.sections.declic_solution.intro}
                    </p>
                  )}
                  <div className="space-y-2">
                    {data.one_page_summary.sections.declic_solution.items?.map((item, idx) => (
                      <p key={idx} className="text-sm text-[#1D1D1D]/80">
                        {typeof item === 'object' ? item.text : item}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Call to Action */}
              {data.one_page_summary.sections?.call_to_action && (
                <div className="md:col-span-2 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg p-4 text-white text-center">
                  <h3 className="font-semibold mb-2">{data.one_page_summary.sections.call_to_action.title}</h3>
                  <p className="text-white/90">{data.one_page_summary.sections.call_to_action.text}</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* BELGIAN CONTEXT ENHANCED (PART 8) */}
        {data?.belgian_context_enhanced && (
          <section className="bg-gradient-to-br from-yellow-50 to-red-50 rounded-xl p-6 border border-yellow-200">
            <h2 className="text-xl font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-red-600" />
              {data.belgian_context_enhanced.title}
            </h2>
            <p className="text-sm text-[#1D1D1D]/70 mb-6">{data.belgian_context_enhanced.intro}</p>
            
            {/* Key Challenges */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {data.belgian_context_enhanced.challenges?.map((challenge, idx) => (
                <div key={idx} className="bg-white rounded-lg p-4 text-center shadow-sm border border-red-100">
                  <p className="text-3xl font-bold text-red-600 mb-1">{challenge.stat}</p>
                  <p className="text-sm font-semibold text-[#1D1D1D] mb-1">{challenge.title}</p>
                  <p className="text-xs text-[#1D1D1D]/60">{challenge.source}</p>
                </div>
              ))}
            </div>
            
            {/* Structural Issues */}
            <div className="bg-white/80 rounded-lg p-4 border-l-4 border-red-500">
              <h3 className="font-semibold text-red-700 mb-3">{language === 'fr' ? 'Problèmes structurels identifiés' : 'Geïdentificeerde structurele problemen'}</h3>
              <div className="space-y-2">
                {data.belgian_context_enhanced.structural_issues?.map((issue, idx) => (
                  <p key={idx} className="text-sm text-[#1D1D1D]/80">{issue}</p>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* OLD SECTION 10: CONCLUSION (Hidden - replaced by strategic_conclusion) */}
        {false && data?.conclusion?.strategic_summary && (
          <section className="bg-gradient-to-br from-[#A4187A] to-[#6A1B5D] rounded-xl p-8 text-white">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Target className="w-7 h-7" />
              {data.conclusion.title}
            </h2>
            
            <p className="text-lg mb-6 opacity-90">{data.conclusion.strategic_summary.main_statement}</p>
            
            {/* Value Propositions */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {data.conclusion.strategic_summary.value_proposition?.map((prop, idx) => (
                <div key={idx} className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                  <h3 className="font-semibold mb-2">{prop.title}</h3>
                  <p className="text-sm opacity-80">{prop.text}</p>
                </div>
              ))}
            </div>
            
            {/* Call to Action */}
            {data.conclusion.strategic_summary.call_to_action && (
              <div className="bg-white/20 rounded-lg p-5 mb-4">
                <h3 className="font-bold text-lg mb-2">{data.conclusion.strategic_summary.call_to_action.title}</h3>
                <p className="opacity-90 mb-2">{data.conclusion.strategic_summary.call_to_action.text}</p>
                <p className="text-sm font-medium opacity-80 italic">{data.conclusion.strategic_summary.call_to_action.final_message}</p>
              </div>
            )}
            
            {/* Closing Quote */}
            {data.conclusion.strategic_summary.closing_quote && (
              <blockquote className="text-center text-lg italic opacity-80 border-t border-white/20 pt-4">
                "{data.conclusion.strategic_summary.closing_quote}"
              </blockquote>
            )}
          </section>
        )}

        {/* SECTION 11: PHRASES SIGNATURES (NEW) */}
        {data?.signature_phrases && (
          <section className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-xl p-6 text-white">
            <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
              <MessageCircle className="w-6 h-6" />
              {data.signature_phrases.title}
            </h2>
            <p className="text-white/70 mb-6 text-sm">{data.signature_phrases.subtitle}</p>
            
            <div className="space-y-6">
              {data.signature_phrases.variants?.map((variant, idx) => (
                <div key={idx} className="bg-white/10 rounded-xl p-5 backdrop-blur-sm border border-white/20 hover:border-white/40 transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs bg-white/20 text-white px-3 py-1 rounded-full">
                      {variant.context}
                    </span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(variant.full);
                        alert(language === 'fr' ? 'Copié dans le presse-papier !' : 'Gekopieerd naar klembord!');
                      }}
                      className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1 rounded-lg transition-colors flex items-center gap-1"
                      title={language === 'fr' ? 'Copier la phrase complète' : 'Kopieer volledige zin'}
                    >
                      <FileText className="w-3 h-3" />
                      {language === 'fr' ? 'Copier' : 'Kopiëren'}
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Hook */}
                    <div className="border-l-4 border-[#FB6823] pl-4">
                      <span className="text-xs text-[#FB6823] font-semibold uppercase tracking-wide">{language === 'fr' ? 'Accroche' : 'Haak'}</span>
                      <p className="text-white font-medium mt-1">"{variant.hook}"</p>
                    </div>
                    
                    {/* Fact */}
                    <div className="border-l-4 border-[#2196F3] pl-4">
                      <span className="text-xs text-[#2196F3] font-semibold uppercase tracking-wide">{language === 'fr' ? 'Constat' : 'Vaststelling'}</span>
                      <p className="text-white/90 mt-1">{variant.fact}</p>
                    </div>
                    
                    {/* Action */}
                    <div className="border-l-4 border-[#4CAF50] pl-4">
                      <span className="text-xs text-[#4CAF50] font-semibold uppercase tracking-wide">{language === 'fr' ? 'Action' : 'Actie'}</span>
                      <p className="text-white/90 mt-1">{variant.action}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* SECTION 12: SOURCES ET RÉFÉRENCES */}
        {visibleSections.sources && data?.sources_references && (
          <section className="bg-white rounded-xl p-6 border border-[#E0E0E0]" data-testid="sources-references">
            <h2 className="text-lg font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
              <span className="w-8 h-8 bg-[#2196F3] text-white rounded-full flex items-center justify-center text-sm font-bold">12</span>
              <BookOpen className="w-5 h-5 text-[#2196F3]" />
              {data.sources_references.title}
            </h2>
            
            {data.sources_references.description && (
              <p className="text-[#1D1D1D]/70 mb-6 text-sm">{data.sources_references.description}</p>
            )}
            
            <div className="space-y-6">
              {data.sources_references.categories?.map((category, idx) => (
                <div key={idx} className="border-l-4 border-[#2196F3] pl-4">
                  <h3 className="font-semibold text-[#2196F3] mb-3">{category.title}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {category.sources?.map((source, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-3 hover:bg-blue-50 transition-colors">
                        <div className="flex items-start justify-between">
                          <h4 className="font-medium text-[#1D1D1D] text-sm">{source.name}</h4>
                          {source.url && (
                            <a 
                              href={source.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-[#2196F3] hover:text-[#1976D2] ml-2 flex-shrink-0"
                              title="Visiter le site"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <p className="text-xs text-[#1D1D1D]/60 mt-1">{source.description}</p>
                        {source.data_used && (
                          <p className="text-xs text-[#2196F3] mt-2">
                            <strong>Données utilisées :</strong> {source.data_used}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            
            {/* Methodology Note */}
            {data.sources_references.methodology_note && (
              <div className="mt-6 p-4 bg-blue-50 border-l-4 border-[#2196F3] rounded-r-lg">
                <p className="text-xs text-[#1D1D1D]/70 italic">
                  <strong>Note méthodologique :</strong> {data.sources_references.methodology_note}
                </p>
              </div>
            )}
          </section>
        )}

        {/* === 3 EXPORT MODES SECTION === */}
        <section className="bg-white rounded-xl overflow-hidden border border-gray-200 no-print" data-testid="pdf-preview-section">
          {/* Section Header */}
          <div className="bg-gradient-to-r from-[#A4187A] via-[#FB6823] to-[#FFC107] p-5 text-white">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Download className="w-5 h-5" />
              {language === 'fr' ? 'Générer votre document' : 'Genereer uw document'}
            </h2>
            <p className="text-white/80 text-sm mt-1">
              {language === 'fr' 
                ? 'Choisissez le format adapté à votre usage : analyse, conviction ou présentation.'
                : 'Kies het formaat dat past bij uw gebruik: analyse, overtuiging of presentatie.'}
            </p>
          </div>

          {/* 3 Export Cards */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              
              {/* CARD 1 — Rapport Complet */}
              <div className="group relative flex flex-col rounded-xl border-2 border-[#2563EB] bg-[#EFF6FF] hover:shadow-lg transition-all overflow-hidden">
                <div className="bg-[#2563EB] px-4 py-3">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-white" />
                    <h3 className="font-bold text-white text-base">
                      {language === 'fr' ? 'Rapport complet' : 'Volledig rapport'}
                    </h3>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-sm text-[#1D1D1D]/70 mb-3">
                    {language === 'fr' 
                      ? 'Document détaillé et structuré pour analyser et diffuser. Style McKinsey/BCG.'
                      : 'Gedetailleerd en gestructureerd document voor analyse en verspreiding.'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#2563EB] mb-4">
                    <span className="px-2 py-0.5 bg-[#2563EB]/10 rounded-full">~20 pages</span>
                    <span className="px-2 py-0.5 bg-[#2563EB]/10 rounded-full">PDF</span>
                    <span className="px-2 py-0.5 bg-[#2563EB]/10 rounded-full">{language === 'fr' ? 'Détaillé' : 'Gedetailleerd'}</span>
                  </div>
                  <button
                    onClick={() => exportDocument('full')}
                    disabled={exportingFormat !== null}
                    className="mt-auto w-full px-4 py-2.5 bg-[#2563EB] text-white rounded-lg font-semibold hover:bg-[#1D4ED8] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="export-full-report-btn"
                  >
                    {exportingFormat === 'full' ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {exportingFormat === 'full' 
                      ? (language === 'fr' ? 'Génération...' : 'Genereren...')
                      : (language === 'fr' ? 'Générer le rapport' : 'Rapport genereren')}
                  </button>
                </div>
              </div>

              {/* CARD 2 — Synthèse Exécutive */}
              <div className="group relative flex flex-col rounded-xl border-2 border-[#A4187A] bg-[#FAF0F7] hover:shadow-lg transition-all overflow-hidden">
                <div className="bg-[#A4187A] px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Rocket className="w-5 h-5 text-white" />
                    <h3 className="font-bold text-white text-base">
                      {language === 'fr' ? 'Synthèse exécutive' : 'Executive samenvatting'}
                    </h3>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-sm text-[#1D1D1D]/70 mb-3">
                    {language === 'fr' 
                      ? 'Compréhension immédiate en 30 secondes. Maximum d\'impact visuel.'
                      : 'Onmiddellijk begrip in 30 seconden. Maximale visuele impact.'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#A4187A] mb-4">
                    <span className="px-2 py-0.5 bg-[#A4187A]/10 rounded-full">1 page</span>
                    <span className="px-2 py-0.5 bg-[#A4187A]/10 rounded-full">PDF</span>
                    <span className="px-2 py-0.5 bg-[#A4187A]/10 rounded-full">{language === 'fr' ? 'Synthétique' : 'Synthetisch'}</span>
                  </div>
                  <button
                    onClick={() => exportDocument('executive')}
                    disabled={exportingFormat !== null}
                    className="mt-auto w-full px-4 py-2.5 bg-[#A4187A] text-white rounded-lg font-semibold hover:bg-[#8E1569] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="export-executive-summary-btn"
                  >
                    {exportingFormat === 'executive' ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {exportingFormat === 'executive' 
                      ? (language === 'fr' ? 'Génération...' : 'Genereren...')
                      : (language === 'fr' ? 'Générer la synthèse' : 'Samenvatting genereren')}
                  </button>
                </div>
              </div>

              {/* CARD 3 — Pitch Deck */}
              <div className="group relative flex flex-col rounded-xl border-2 border-[#FB6823] bg-[#FFF7ED] hover:shadow-lg transition-all overflow-hidden">
                <div className="bg-[#FB6823] px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-white" />
                    <h3 className="font-bold text-white text-base">
                      {language === 'fr' ? 'Pitch deck' : 'Pitch deck'}
                    </h3>
                  </div>
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-sm text-[#1D1D1D]/70 mb-3">
                    {language === 'fr' 
                      ? 'Présentation orale percutante pour financeurs, partenaires et décideurs.'
                      : 'Krachtige mondelinge presentatie voor financiers, partners en besluitvormers.'}
                  </p>
                  <div className="flex items-center gap-2 text-xs text-[#FB6823] mb-4">
                    <span className="px-2 py-0.5 bg-[#FB6823]/10 rounded-full">~10 slides</span>
                    <span className="px-2 py-0.5 bg-[#FB6823]/10 rounded-full">PDF</span>
                    <span className="px-2 py-0.5 bg-[#FB6823]/10 rounded-full">{language === 'fr' ? 'Narratif' : 'Verhalend'}</span>
                  </div>
                  <button
                    onClick={() => exportDocument('pitch')}
                    disabled={exportingFormat !== null}
                    className="mt-auto w-full px-4 py-2.5 bg-[#FB6823] text-white rounded-lg font-semibold hover:bg-[#E55A1B] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="export-pitch-deck-btn"
                  >
                    {exportingFormat === 'pitch' ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {exportingFormat === 'pitch' 
                      ? (language === 'fr' ? 'Génération...' : 'Genereren...')
                      : (language === 'fr' ? 'Générer le pitch' : 'Pitch genereren')}
                  </button>
                </div>
              </div>
            </div>

            {/* Embedded PDF Viewer toggle */}
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
              <button
                onClick={loadPdfPreview}
                className="px-4 py-2 bg-gray-100 text-[#1D1D1D]/70 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm"
                data-testid="toggle-pdf-viewer-btn"
              >
                {showPdfViewer ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showPdfViewer 
                  ? (language === 'fr' ? 'Masquer l\'aperçu' : 'Voorbeeld verbergen')
                  : (language === 'fr' ? 'Aperçu du rapport complet' : 'Volledig rapport voorbeeld')}
              </button>
              <p className="text-xs text-[#1D1D1D]/40">
                {language === 'fr' 
                  ? 'Les 3 formats sont cohérents et générés depuis les mêmes données.'
                  : 'De 3 formaten zijn consistent en gegenereerd op basis van dezelfde gegevens.'}
              </p>
            </div>
          </div>

          {/* Embedded PDF Viewer */}
          {showPdfViewer && pdfPreviewUrl && (
            <div className="border-t border-gray-200 p-4" data-testid="pdf-embedded-viewer">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-[#1D1D1D] flex items-center gap-2">
                  <Eye className="w-4 h-4 text-[#FB6823]" />
                  {language === 'fr' ? 'Aperçu du rapport complet' : 'Voorbeeld volledig rapport'}
                </p>
                <button
                  onClick={() => setShowPdfViewer(false)}
                  className="text-xs text-[#1D1D1D]/50 hover:text-[#1D1D1D] transition-colors flex items-center gap-1"
                >
                  <EyeOff className="w-3 h-3" />
                  {language === 'fr' ? 'Fermer' : 'Sluiten'}
                </button>
              </div>
              <object
                data={pdfPreviewUrl}
                type="application/pdf"
                className="w-full rounded-lg border border-gray-200"
                style={{ height: '700px' }}
              >
                <div className="flex flex-col items-center justify-center h-full bg-gray-50 rounded-lg p-8 text-center" style={{ height: '700px' }}>
                  <FileText className="w-16 h-16 text-[#FB6823]/30 mb-4" />
                  <p className="text-[#1D1D1D]/70 mb-2">
                    {language === 'fr' 
                      ? 'Votre navigateur ne supporte pas l\'affichage PDF intégré.' 
                      : 'Uw browser ondersteunt geen ingebouwde PDF-weergave.'}
                  </p>
                  <a
                    href={pdfPreviewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-5 py-2.5 bg-gradient-to-r from-[#FB6823] to-[#FFC107] text-white rounded-lg font-medium hover:shadow-lg transition-all flex items-center gap-2 mt-2"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {language === 'fr' ? 'Ouvrir le PDF dans un nouvel onglet' : 'Open de PDF in een nieuw tabblad'}
                  </a>
                </div>
              </object>
            </div>
          )}
        </section>

        {/* Footer */}
        <footer className="text-center text-sm text-[#1D1D1D]/50 py-6 print:py-2">
          <p>Document généré automatiquement par Déclic</p>
          <p className="mt-1">© {new Date().getFullYear()} Ikigai Déclic - Tous droits réservés</p>
        </footer>
      </main>
    </div>
  );
}
